import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ConnectionService } from '../connection/connection.service';
import { AppConstants } from '../../entity/constants';
import { MovedInOutApiItem, MovedInOutRow, RouteSummary, TaskTimeWindow } from '../../models/moved-in-out-item';
import { PlannedAtLoadingApiItem, PlannedAtLoadingRow } from '../../models/planned-at-loading-item';
import { DateFormatPipe, DateFormatType } from '../../pipe/date-format-pipe/date-format.pipe';
import { map } from 'rxjs/operators';

export interface ItemDetail {
  [key: string]: unknown;
}

export interface ItemDetailsResponse<TItem = ItemDetail> {
  items: TItem[];
  [key: string]: unknown;
}

@Injectable({
  providedIn: 'root'
})
export class ItemsDetailsService {

  constructor(
    private readonly connectionService: ConnectionService,
    private readonly dateFormatPipe: DateFormatPipe
  ) { }

  /**
   * Fetch items details for a specific route and category
   * @param routeId - The route ID
   * @param category - The category (MOVED_IN or MOVED_OUT)
    * @returns Observable of ItemDetailsResponse
   */
  getItemsDetails<TItem = ItemDetail>(routeId: string, category: string): Observable<ItemDetailsResponse<TItem>> {
    const endUrl = `${routeId}/itemsDetails/${category}`;
    
    return this.connectionService.httpReq({
      reqType: AppConstants.GETREQUEST,
      endUrl: endUrl,
      directoryType: AppConstants.DP,
      isLoader: false
    }) as Observable<ItemDetailsResponse<TItem>>;
  }

  /**
   * Fetch moved-in items for a route
   * @param routeId - The route ID
    * @returns Observable of ItemDetailsResponse
   */
    getMovedInItems(routeId: string): Observable<ItemDetailsResponse<MovedInOutApiItem>> {
    return this.getItemsDetails<MovedInOutApiItem>(routeId, AppConstants.ITEMSDETAILS_MOVED_IN);
  }

  /**
   * Fetch moved-out items for a route
   * @param routeId - The route ID
   * @returns Observable of ItemDetailsResponse
   */
  getMovedOutItems(routeId: string): Observable<ItemDetailsResponse<MovedInOutApiItem>> {
    return this.getItemsDetails<MovedInOutApiItem>(routeId, AppConstants.ITEMSDETAILS_MOVED_OUT);
  }

  getMovedInRows(routeId: string): Observable<MovedInOutRow[]> {
    return this.getMovedInItems(routeId).pipe(
      map((response) => this.formatMovedInOutRows(response.items ?? [], false))
    );
  }

  getMovedOutRows(routeId: string): Observable<MovedInOutRow[]> {
    return this.getMovedOutItems(routeId).pipe(
      map((response) => this.formatMovedInOutRows(response.items ?? [], true))
    );
  }

  /**
   * Fetch planned-after-load-start items for a route
   * @param routeId - The route ID
   * @returns Observable of ItemDetailsResponse
   */
  getPlannedAfterLoadStartItems(routeId: string): Observable<ItemDetailsResponse<PlannedAtLoadingApiItem>> {
    return this.getItemsDetails<PlannedAtLoadingApiItem>(routeId, AppConstants.ITEMSDETAILS_PLANNED_AFTER_LOADSTART);
  }
  
  /**
   * Fetch planned-at-loading rows for a route
   * @param routeId - The route ID
   * @returns Observable of PlannedAtLoadingRow[]
   */
  getPlannedAtLoadingRows(routeId: string): Observable<PlannedAtLoadingRow[]> {
    return this.getPlannedAfterLoadStartItems(routeId).pipe(
      map((response) => this.formatPlannedAtLoadingRows(response.items ?? []))
    );
  }

  /**
   * Format moved-in or moved-out rows for a route
   * @param rows - The array of MovedInOutApiItem
   * @param useCurrentRoute - Flag to determine whether to use the current route or planned route
   * @returns Array of MovedInOutRow
   */
  private formatMovedInOutRows(rows: MovedInOutApiItem[], useCurrentRoute: boolean): MovedInOutRow[] {
    return rows.map((row) => this.mapMovedInOutRow(row, useCurrentRoute));
  }

  /**
   * Map a moved-in or moved-out API item to a MovedInOutRow
   * @param row - The MovedInOutApiItem to map
   * @param useCurrentRoute - Flag to determine whether to use the current route or planned route
   * @returns The mapped MovedInOutRow
   */
  private mapMovedInOutRow(row: MovedInOutApiItem, useCurrentRoute: boolean): MovedInOutRow {
    const routeInfo = useCurrentRoute ? row.current_route : row.planned_route;

    return this.mapCommonRow(row, routeInfo);
  }

  /**
   * Format the additional service code for a moved-in or moved-out item
   * @param additionalServiceCode - The additional service code(s) to format
   * @returns The formatted additional service code as a string
   */
  private formatAdditionalServiceCode(additionalServiceCode: string | string[] | null | undefined): string {
    if (Array.isArray(additionalServiceCode)) {
      return additionalServiceCode.join(',');
    }
    return additionalServiceCode ?? '';
  }

  /**
   * Format planned-at-loading rows for a route
   * @param rows - The array of PlannedAtLoadingApiItem
   * @returns Array of PlannedAtLoadingRow
   */
  private formatPlannedAtLoadingRows(rows: PlannedAtLoadingApiItem[]): PlannedAtLoadingRow[] {
    return rows.map((row) => {
      const routeInfo = row.planned_route ?? row.current_route;

      return this.mapCommonRow(row, routeInfo);
    });
  }

  private mapCommonRow(row: MovedInOutApiItem, routeInfo?: RouteSummary): MovedInOutRow {
    return {
      ...row,
      route_id: routeInfo?.route_id ?? '',
      route_name: routeInfo?.route_name ?? '',
      bl_name: routeInfo?.bl_name ?? '',
      receiver_name: row.consignee_name ?? '',
      address: row.consignee_address ?? '',
      barcode: row.barcode ?? '',
      time_window: this.formatTimeWindow(row.task_time_window),
      asc: this.formatAdditionalServiceCode(row.additional_service_code)
    };
  }

  /**
   * Format a time window value
   * @param value - The time window value to format
   * @returns The formatted time window as a string
   */
  private formatTimeWindow(value: TaskTimeWindow | string | null | undefined): string {
    if (!value) {
      return '';
    }

    if (typeof value === 'string') {
      return value;
    }

    const { begin, end } = value;
    if (!begin || !end) {
      return '';
    }

    const beginTime = this.dateFormatPipe.transform(begin, DateFormatType.TIME) ?? '';
    const endTime = this.dateFormatPipe.transform(end, DateFormatType.TIME) ?? '';
    return `${beginTime} - ${endTime}`;
  }
}
